import { compact } from 'lodash';

import { DpRecipientSuggestionFragment } from '@/modules/aiDP/graphql/aiDP.generated';
import { isValidReferenceAISuggestionKind } from '@/modules/aiOnboarding/aiOnboarding.utils';
import { MatcherSuggestion } from '@/modules/aiSuggestions/aiSuggestions.contracts';
import {
  AISuggestionMatch,
  AISuggestionsMatcherVariant,
} from '@/modules/aiSuggestions/AISuggestionsMatcher/AISuggestionsMatcher.types';
import { CommonAiSuggestionActorFragment } from '@/modules/aiSuggestions/graphql/aiSuggestions.generated';
import {
  getFirstResolvedDataFromNode,
  getOwningEntityFromSuggestionStatements,
  ResolvedDataKind,
  SuggestionForKGV2Grouping,
} from '@/modules/aiSuggestions/kgV2Resolution.utils';
import { ClientProfileNameFields } from '@/modules/clientProfiles/ClientProfile.types';
import { SURVIVING_SPOUSE_SENTINEL } from '@/modules/entities/EntityBeneficiariesSubform/EntityBeneficiariesSubform.constants';
import {
  AiSuggestionKind,
  BeneficiaryKind,
  DispositiveProvisionRecipientKind,
} from '@/types/schema';
import { diagnostics } from '@/utils/diagnostics';
import { getNodes } from '@/utils/graphqlUtils';
import { getPulidKind, PulidKind } from '@/utils/pulid';

export function getSuggestedActorDisplayName(
  suggestion: MatcherSuggestion
): string {
  switch (suggestion.kind) {
    case AiSuggestionKind.Beneficiary:
      return formatSuggestedPersonDisplayName(suggestion.beneficiary);
    case AiSuggestionKind.BeneficiaryV2:
      return formatSuggestedPersonDisplayName(suggestion.beneficiaryV2);
    case AiSuggestionKind.Principal:
      return formatSuggestedPersonDisplayName(suggestion.principal);
    case AiSuggestionKind.TrustAdvisor:
      return formatSuggestedPersonDisplayName(suggestion.trustAdvisor);
    case AiSuggestionKind.Trustee:
      return formatSuggestedPersonDisplayName(suggestion.trustee);
    case AiSuggestionKind.SuccessorTrustee:
      return formatSuggestedPersonDisplayName(suggestion.successorTrustee);
    case AiSuggestionKind.BeneficialOwner:
      return formatSuggestedPersonDisplayName(suggestion.beneficialOwner);
    case AiSuggestionKind.KeyPerson:
      return formatSuggestedPersonDisplayName(suggestion.keyPerson);
    case AiSuggestionKind.DpRecipientGrantor_1DiesFirst:
    case AiSuggestionKind.DpRecipientGrantor_2DiesFirst:
    case AiSuggestionKind.TeDpRecipientGrantor_1DiesFirst:
    case AiSuggestionKind.TeDpRecipientGrantor_2DiesFirst:
    case AiSuggestionKind.DpRecipientV2:
    case AiSuggestionKind.DpRecipientTestamentaryEntity: // TODO: Delete. We don't suggest this anymore.
      return formatSuggestedDPRecipientDisplayName(suggestion.dpRecipient);
    // Subtrust suggestions
    case AiSuggestionKind.SubtrustTrustee:
      return formatSuggestedPersonDisplayName(suggestion.subtrustTrustee);
    case AiSuggestionKind.SubtrustSuccessorTrustee:
      return formatSuggestedPersonDisplayName(
        suggestion.subtrustSuccessorTrustee
      );
    case AiSuggestionKind.SubtrustTrustAdvisor:
      return formatSuggestedPersonDisplayName(suggestion.subtrustTrustAdvisor);
    case AiSuggestionKind.SubtrustLifetimeBeneficiary:
      if (
        suggestion.subtrustLifetimeBeneficiary?.beneficiaryKind ===
        BeneficiaryKind.SurvivingSpouse
      ) {
        return 'Surviving spouse';
      }
      return formatSuggestedPersonDisplayName(
        suggestion.subtrustLifetimeBeneficiary
      );
    case AiSuggestionKind.SubtrustRemainderBeneficiary:
      if (
        suggestion.subtrustRemainderBeneficiary?.beneficiaryKind ===
        BeneficiaryKind.SurvivingSpouse
      ) {
        return 'Surviving spouse';
      }
      return formatSuggestedPersonDisplayName(
        suggestion.subtrustRemainderBeneficiary
      );
    default:
      return `Unknown suggestion of type ${suggestion.kind}`;
  }
}

export function formatSuggestedPersonDisplayName(
  actor:
    | {
        firstName?: string | null;
        middleName?: string | null;
        lastName?: string | null;
        suffix?: string | null;
        organizationName?: string | null;
      }
    | undefined
    | null
): string {
  if (!actor) {
    const msg = 'Actor is undefined or null';
    diagnostics.error(msg, new Error(msg));
    return 'Unknown suggestion';
  }

  if (actor?.organizationName) {
    return actor.organizationName;
  }

  return compact([
    actor.firstName,
    actor.middleName,
    actor.lastName,
    actor.suffix,
  ]).join(' ');
}

function formatSuggestedDPRecipientDisplayName(
  dpRecipient: DpRecipientSuggestionFragment | undefined | null
) {
  if (dpRecipient?.recipientEntityName) {
    return dpRecipient.recipientEntityName;
  }

  if (dpRecipient?.recipientOrganizationName) {
    return dpRecipient.recipientOrganizationName;
  }

  if (!dpRecipient?.recipientNameParts) {
    diagnostics.error(
      "DP Recipient Suggestion doesn't have entity, org, or person name"
    );
    return 'Unknown DP Recipient Name';
  }

  return compact([
    dpRecipient?.recipientNameParts?.firstName,
    dpRecipient?.recipientNameParts?.middleName,
    dpRecipient?.recipientNameParts?.lastName,
    dpRecipient?.recipientNameParts?.suffix,
  ]).join(' ');
}

const DEFAULT_EMPTY_NAME: {
  initialClientProfileName: ClientProfileNameFields;
  initialClientOrganizationName: string;
  initialEntityName: string;
} = {
  initialClientProfileName: {
    firstName: '',
    middleName: '',
    lastName: '',
    suffix: '',
  },
  initialClientOrganizationName: '',
  initialEntityName: '',
};

export function addNewAnythingFromSuggestionName(
  suggestion: MatcherSuggestion
): {
  initialClientProfileName: ClientProfileNameFields;
  initialClientOrganizationName: string;
  initialEntityName: string;
} {
  // Return the initial name values for the addNewAnything modal from the
  // suggested DP Recipient's name parts.
  if (
    suggestion.kind === AiSuggestionKind.DpRecipientGrantor_1DiesFirst ||
    suggestion.kind === AiSuggestionKind.DpRecipientGrantor_2DiesFirst ||
    suggestion.kind === AiSuggestionKind.TeDpRecipientGrantor_1DiesFirst ||
    suggestion.kind === AiSuggestionKind.TeDpRecipientGrantor_2DiesFirst ||
    suggestion.kind === AiSuggestionKind.DpRecipientV2 ||
    suggestion.kind === AiSuggestionKind.DpRecipientTestamentaryEntity
  ) {
    const dpRecipient = suggestion.dpRecipient;
    if (!dpRecipient) {
      return DEFAULT_EMPTY_NAME;
    }

    return {
      initialClientProfileName: {
        firstName: dpRecipient.recipientNameParts?.firstName ?? '',
        middleName: dpRecipient.recipientNameParts?.middleName ?? '',
        lastName: dpRecipient.recipientNameParts?.lastName ?? '',
        suffix: dpRecipient.recipientNameParts?.suffix ?? '',
      },
      initialClientOrganizationName:
        dpRecipient.recipientOrganizationName ?? '',
      initialEntityName: dpRecipient.recipientEntityName ?? '',
    };
  }

  // Return the initial name values for the addNewAnything modal from the
  // suggested actor's name parts.

  let actor: CommonAiSuggestionActorFragment | null | undefined = null;

  switch (suggestion.kind) {
    case AiSuggestionKind.BeneficiaryV2:
      actor = suggestion.beneficiaryV2;
      break;
    case AiSuggestionKind.Principal:
      actor = suggestion.principal;
      break;
    case AiSuggestionKind.TrustAdvisor:
      actor = suggestion.trustAdvisor;
      break;
    case AiSuggestionKind.Trustee:
      actor = suggestion.trustee;
      break;
    case AiSuggestionKind.SuccessorTrustee:
      actor = suggestion.successorTrustee;
      break;
    case AiSuggestionKind.BeneficialOwner:
      actor = suggestion.beneficialOwner;
      break;
    case AiSuggestionKind.KeyPerson:
      actor = suggestion.keyPerson;
      break;
    default:
      return DEFAULT_EMPTY_NAME;
  }

  if (!actor) {
    return DEFAULT_EMPTY_NAME;
  }

  const { firstName, middleName, lastName, suffix } = actor;

  return {
    initialClientProfileName: {
      firstName: firstName ?? '',
      middleName: middleName ?? '',
      lastName: lastName ?? '',
      suffix: suffix ?? '',
    },
    initialClientOrganizationName: actor.organizationName ?? '',
    initialEntityName: actor.organizationName ?? '',
  };
}

/**
 * A match is "actionable" if it carries enough information to update the form:
 * either a PULID for an existing record (clientProfile / clientOrganization /
 * entity / testamentaryEntity), or the surviving-spouse sentinel. Without one
 * of these, the row was confirmed without a selection and should be treated as
 * a rejection rather than applied to the form.
 */
export function isActionableSuggestionMatch(
  match: Pick<
    AISuggestionMatch,
    | 'clientProfileId'
    | 'clientOrganizationId'
    | 'entityId'
    | 'testamentaryId'
    | 'survivingSpouse'
  >
): boolean {
  return Boolean(
    match.clientProfileId ||
      match.clientOrganizationId ||
      match.entityId ||
      match.testamentaryId ||
      match.survivingSpouse
  );
}

export function getMatchFromNewId(
  newId: string
):
  | { clientProfileId: string }
  | { clientOrganizationId: string }
  | { entityId: string }
  | { testamentaryId: string }
  | { survivingSpouse: true }
  | null {
  if (newId === SURVIVING_SPOUSE_SENTINEL) {
    return {
      survivingSpouse: true,
    };
  }

  const selectedPulidKind = getPulidKind(newId);

  if (selectedPulidKind === PulidKind.ClientProfile) {
    return {
      clientProfileId: newId,
    };
  }

  if (selectedPulidKind === PulidKind.ClientOrganization) {
    return {
      clientOrganizationId: newId,
    };
  }

  if (selectedPulidKind === PulidKind.Entity) {
    return {
      entityId: newId,
    };
  }

  if (selectedPulidKind === PulidKind.TestamentaryEntity) {
    return {
      testamentaryId: newId,
    };
  }

  return null;
}

/**
 * For subtrust beneficiary suggestions, returns the surviving-spouse sentinel
 * if the backend tagged the suggestion with `BeneficiaryKind.SurvivingSpouse`.
 * Returns undefined for any other suggestion kind or beneficiary kind.
 */
export function getSuggestionMatchIDFromSurvivingSpouse(
  suggestion: MatcherSuggestion
): string | undefined {
  switch (suggestion.kind) {
    case AiSuggestionKind.SubtrustLifetimeBeneficiary:
      return suggestion.subtrustLifetimeBeneficiary?.beneficiaryKind ===
        BeneficiaryKind.SurvivingSpouse
        ? SURVIVING_SPOUSE_SENTINEL
        : undefined;
    case AiSuggestionKind.SubtrustRemainderBeneficiary:
      return suggestion.subtrustRemainderBeneficiary?.beneficiaryKind ===
        BeneficiaryKind.SurvivingSpouse
        ? SURVIVING_SPOUSE_SENTINEL
        : undefined;
    default:
      return undefined;
  }
}

export function getSuggestionMatchIDFromExistingIDField(
  suggestion: MatcherSuggestion
): string | undefined | null {
  switch (suggestion.kind) {
    case AiSuggestionKind.BeneficiaryV2:
      return suggestion.beneficiaryV2?.existingID;
    case AiSuggestionKind.Principal:
      return suggestion.principal?.existingID;
    case AiSuggestionKind.TrustAdvisor:
      return suggestion.trustAdvisor?.existingID;
    case AiSuggestionKind.Trustee:
      return suggestion.trustee?.existingID;
    case AiSuggestionKind.SuccessorTrustee:
      return suggestion.successorTrustee?.existingID;
    case AiSuggestionKind.BeneficialOwner:
      return suggestion.beneficialOwner?.existingID;
    case AiSuggestionKind.KeyPerson:
      return suggestion.keyPerson?.existingID;
    case AiSuggestionKind.DpRecipientGrantor_1DiesFirst:
    case AiSuggestionKind.DpRecipientGrantor_2DiesFirst:
    case AiSuggestionKind.TeDpRecipientGrantor_1DiesFirst:
    case AiSuggestionKind.TeDpRecipientGrantor_2DiesFirst:
    case AiSuggestionKind.DpRecipientV2:
    case AiSuggestionKind.DpRecipientTestamentaryEntity:
      return suggestion.dpRecipient?.recipientID;
    default:
      return undefined;
  }
}

export function getSuggestionMatchIDFromReferenceOnboardingSuggestionField(
  suggestion: MatcherSuggestion
): string | undefined | null {
  if (
    !suggestion?.referenceAiOnboardingSuggestion?.kind ||
    !isValidReferenceAISuggestionKind(
      suggestion?.referenceAiOnboardingSuggestion?.kind
    )
  ) {
    return undefined;
  }

  return (
    suggestion.referenceAiOnboardingSuggestion?.onboardedIndividual?.id ||
    suggestion.referenceAiOnboardingSuggestion?.onboardedOrganization?.id ||
    suggestion.referenceAiOnboardingSuggestion?.onboardedEntity?.id ||
    suggestion.referenceAiOnboardingSuggestion?.onboardedTestamentaryEntity?.id
  );
}

const DEFAULT_DP_RECIPIENT_RESOLVED_DATA_KINDS: ResolvedDataKind[] = [
  'entity',
  'testamentaryEntity',
  'clientProfile',
  'clientOrganization',
];

function getDpRecipientResolvedDataKinds(
  recipientKind: DispositiveProvisionRecipientKind | null | undefined
): ResolvedDataKind[] {
  // Prefer the resolved record type that matches the suggested recipient kind
  // so the matcher preselects the expected dropdown option before falling back
  // to any other resolved record on the same KGV2 node.
  switch (recipientKind) {
    case DispositiveProvisionRecipientKind.Individual:
    case DispositiveProvisionRecipientKind.SurvivingSpouse:
      return [
        'clientProfile',
        'entity',
        'testamentaryEntity',
        'clientOrganization',
      ];
    case DispositiveProvisionRecipientKind.Organization:
      return [
        'clientOrganization',
        'clientProfile',
        'entity',
        'testamentaryEntity',
      ];
    case DispositiveProvisionRecipientKind.Entity:
      return [
        'entity',
        'testamentaryEntity',
        'clientProfile',
        'clientOrganization',
      ];
    case DispositiveProvisionRecipientKind.TestamentaryEntity:
      return [
        'testamentaryEntity',
        'entity',
        'clientProfile',
        'clientOrganization',
      ];
    default:
      return DEFAULT_DP_RECIPIENT_RESOLVED_DATA_KINDS;
  }
}

export function getSuggestionMatchIDFromDpRecipientKGV2Nodes(
  suggestion: MatcherSuggestion
): string | undefined {
  if (suggestion.kind !== AiSuggestionKind.DpRecipientV2) {
    return undefined;
  }

  const resolvedDataKinds = getDpRecipientResolvedDataKinds(
    suggestion.dpRecipient?.recipientKind
  );

  for (const node of getNodes(suggestion.dpRecipientKgV2Nodes)) {
    const resolvedMatchId = getFirstResolvedDataFromNode(
      node,
      resolvedDataKinds
    )?.id;
    if (resolvedMatchId) {
      return resolvedMatchId;
    }
  }

  return undefined;
}

function getKGV2ResolvedDataKindsForSuggestionMatch(
  suggestionKind: AiSuggestionKind
): ResolvedDataKind[] | undefined {
  switch (suggestionKind) {
    case AiSuggestionKind.Principal:
      return ['clientProfile'];
    case AiSuggestionKind.Trustee:
    case AiSuggestionKind.SuccessorTrustee:
    case AiSuggestionKind.TrustAdvisor:
    case AiSuggestionKind.SubtrustTrustee:
    case AiSuggestionKind.SubtrustSuccessorTrustee:
    case AiSuggestionKind.SubtrustTrustAdvisor:
      return ['clientProfile', 'clientOrganization'];
    case AiSuggestionKind.BeneficiaryV2:
    case AiSuggestionKind.SubtrustLifetimeBeneficiary:
    case AiSuggestionKind.SubtrustRemainderBeneficiary:
      return ['clientProfile', 'clientOrganization', 'entity'];
    case AiSuggestionKind.BeneficialOwner:
    case AiSuggestionKind.KeyPerson:
      return ['clientProfile', 'entity', 'clientOrganization'];
    default:
      return undefined;
  }
}

export function getSuggestionMatchIDFromKGV2Statements(
  suggestion: MatcherSuggestion
): string | undefined {
  const resolvedDataKinds = getKGV2ResolvedDataKindsForSuggestionMatch(
    suggestion.kind
  );

  if (!resolvedDataKinds) {
    return undefined;
  }

  return (
    getOwningEntityFromSuggestionStatements(
      suggestion as SuggestionForKGV2Grouping,
      {
        referenceType: 'subject',
        resolvedDataKinds,
      }
    ) || undefined
  );
}

export function isDPMatcherVariant(
  matcherVariant: AISuggestionsMatcherVariant
) {
  return (
    matcherVariant ===
      AISuggestionsMatcherVariant.DP_RECIPIENTS_GRANTOR_1_DIES_FIRST ||
    matcherVariant ===
      AISuggestionsMatcherVariant.DP_RECIPIENTS_GRANTOR_2_DIES_FIRST ||
    matcherVariant ===
      AISuggestionsMatcherVariant.DP_RECIPIENTS_GRANTOR_1_DIES_FIRST_FIRST_DEATH ||
    matcherVariant ===
      AISuggestionsMatcherVariant.DP_RECIPIENTS_GRANTOR_1_DIES_FIRST_SECOND_DEATH ||
    matcherVariant ===
      AISuggestionsMatcherVariant.DP_RECIPIENTS_GRANTOR_2_DIES_FIRST_FIRST_DEATH ||
    matcherVariant ===
      AISuggestionsMatcherVariant.DP_RECIPIENTS_GRANTOR_2_DIES_FIRST_SECOND_DEATH ||
    matcherVariant ===
      AISuggestionsMatcherVariant.TE_DP_RECIPIENTS_GRANTOR_1_DIES_FIRST ||
    matcherVariant ===
      AISuggestionsMatcherVariant.TE_DP_RECIPIENTS_GRANTOR_1_DIES_FIRST_FIRST_DEATH ||
    matcherVariant ===
      AISuggestionsMatcherVariant.TE_DP_RECIPIENTS_GRANTOR_1_DIES_FIRST_SECOND_DEATH ||
    matcherVariant ===
      AISuggestionsMatcherVariant.TE_DP_RECIPIENTS_GRANTOR_2_DIES_FIRST ||
    matcherVariant ===
      AISuggestionsMatcherVariant.TE_DP_RECIPIENTS_GRANTOR_2_DIES_FIRST_FIRST_DEATH ||
    matcherVariant ===
      AISuggestionsMatcherVariant.TE_DP_RECIPIENTS_GRANTOR_2_DIES_FIRST_SECOND_DEATH
  );
}

/**
 * Returns the placeholder text for the row selection dropdown in the matcher.
 * @param suggestionKind
 */
export function getMatcherRowSelectionPlaceholder(
  suggestionKind: AiSuggestionKind
): string {
  switch (suggestionKind) {
    case AiSuggestionKind.Beneficiary:
    case AiSuggestionKind.BeneficiaryV2:
    case AiSuggestionKind.SubtrustLifetimeBeneficiary:
    case AiSuggestionKind.SubtrustRemainderBeneficiary:
      return 'individual';
    case AiSuggestionKind.Principal:
      return 'grantor';
    case AiSuggestionKind.TrustAdvisor:
    case AiSuggestionKind.SubtrustTrustAdvisor:
      return 'trust advisor';
    case AiSuggestionKind.Trustee:
    case AiSuggestionKind.SuccessorTrustee:
    case AiSuggestionKind.SubtrustTrustee:
    case AiSuggestionKind.SubtrustSuccessorTrustee:
      return 'trustee';
    case AiSuggestionKind.BeneficialOwner:
      return 'owner';
    case AiSuggestionKind.KeyPerson:
      return 'key person';
    case AiSuggestionKind.DpRecipientGrantor_1DiesFirst:
    case AiSuggestionKind.DpRecipientGrantor_2DiesFirst:
    case AiSuggestionKind.TeDpRecipientGrantor_1DiesFirst:
    case AiSuggestionKind.TeDpRecipientGrantor_2DiesFirst:
    case AiSuggestionKind.DpRecipientV2:
    case AiSuggestionKind.DpRecipientTestamentaryEntity:
      return 'recipient';
    default:
      return '';
  }
}
